import { useState, useEffect } from "react";
import { Bell, Download, X } from "lucide-react";
import { notificationsApi } from "../../lib/api";
import { notify } from "../../lib/notify";

// ─── Push Notification Prompt ───────────────────────────────────────────────

const PUSH_DENIAL_KEY = "push_denial_count";
const PUSH_DENIED_AT_KEY = "push_denied_at";
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

function safeGet(key: string): string | null {
	try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
	try { localStorage.setItem(key, value); } catch {}
}

function shouldShowPush(): boolean {
	if (!("PushManager" in window)) return false;
	if (typeof Notification !== "undefined" && Notification.permission === "granted") return false;
	const denials = Number(safeGet(PUSH_DENIAL_KEY) ?? 0);
	if (denials >= 5) return false;
	const deniedAt = safeGet(PUSH_DENIED_AT_KEY);
	if (deniedAt && Date.now() - Number(deniedAt) < THREE_DAYS_MS) return false;
	return true;
}

function shouldShowInstall(): boolean {
	// Check if already installed as PWA
	if (window.matchMedia("(display-mode: standalone)").matches) return false;
	if ((navigator as Navigator & { standalone?: boolean }).standalone) return false;
	if (safeGet("app_install_dismissed")) return false;
	return true;
}

// Capture the beforeinstallprompt event for triggering PWA install
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

if (typeof window !== "undefined") {
	window.addEventListener("beforeinstallprompt", (e) => {
		e.preventDefault();
		deferredInstallPrompt = e as BeforeInstallPromptEvent;
	});
}

export function SidebarPrompts() {
	const [showPush, setShowPush] = useState(false);
	const [showInstall, setShowInstall] = useState(false);
	const [enabling, setEnabling] = useState(false);
	const [isIos] = useState(() => /iPhone|iPad/.test(navigator.userAgent));

	useEffect(() => {
		setShowPush(shouldShowPush());
		setShowInstall(shouldShowInstall());
	}, []);

	const handleEnablePush = async () => {
		setEnabling(true);
		try {
			const permission = await Notification.requestPermission();
			if (permission === "granted") {
				const reg = await Promise.race([
					navigator.serviceWorker.ready,
					new Promise<never>((_, reject) =>
						setTimeout(() => reject(new Error("SW not available")), 10_000),
					),
				]);
				const vapidRes = await notificationsApi.getVapidPublicKey();
				const publicKey = vapidRes.data?.publicKey;
				if (publicKey) {
					const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
					const base64 = (publicKey + padding).replace(/-/g, "+").replace(/_/g, "/");
					const rawData = atob(base64);
					const arr = new Uint8Array(rawData.length);
					for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);

					const sub = await reg.pushManager.subscribe({
						userVisibleOnly: true,
						applicationServerKey: arr.buffer as ArrayBuffer,
					});
					const json = sub.toJSON();
					const keys = json.keys as { p256dh: string; auth: string } | undefined;
					if (keys?.p256dh && keys?.auth) {
						await notificationsApi.subscribe({
							endpoint: sub.endpoint,
							p256dh: keys.p256dh,
							auth: keys.auth,
						});
					}
				}
				notify.success("Notifications enabled!");
				setShowPush(false);
			} else {
				const count = Number(safeGet(PUSH_DENIAL_KEY) ?? 0);
				safeSet(PUSH_DENIAL_KEY, String(count + 1));
				safeSet(PUSH_DENIED_AT_KEY, String(Date.now()));
				setShowPush(false);
			}
		} catch {
			notify.error("Could not enable notifications");
		} finally {
			setEnabling(false);
		}
	};

	const handleDismissPush = () => {
		const count = Number(safeGet(PUSH_DENIAL_KEY) ?? 0);
		safeSet(PUSH_DENIAL_KEY, String(count + 1));
		safeSet(PUSH_DENIED_AT_KEY, String(Date.now()));
		setShowPush(false);
	};

	const handleDismissInstall = () => {
		safeSet("app_install_dismissed", "1");
		setShowInstall(false);
	};

	if (!showPush && !showInstall) return null;

	return (
		<div className="space-y-1 mb-2">
			{showPush && (
				<div className="group flex items-center rounded-md px-3 py-2 text-sm transition-colors bg-transparent border border-primary-200/60 dark:border-primary-800/60">
					<Bell className="mr-2.5 h-4 w-4 flex-shrink-0 text-primary-600" />
					<button
						type="button"
						onClick={handleEnablePush}
						disabled={enabling}
						className="flex-1 text-left text-xs font-medium text-primary-700 dark:text-primary-300 hover:underline disabled:opacity-50"
					>
						{enabling ? "Enabling..." : "Turn on lead alerts"}
					</button>
					<button
						type="button"
						onClick={handleDismissPush}
						className="ml-1 p-0.5 text-primary-400 hover:text-primary-600"
						aria-label="Dismiss"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</div>
			)}
			{showInstall && (
				<div className="group flex items-center rounded-md px-3 py-2 text-sm transition-colors bg-transparent border border-info/40">
					<Download className="mr-2.5 h-4 w-4 flex-shrink-0 text-info" />
					<button
						type="button"
						onClick={async () => {
							if (deferredInstallPrompt) {
								await deferredInstallPrompt.prompt();
								const { outcome } = await deferredInstallPrompt.userChoice;
								if (outcome === "accepted") {
									setShowInstall(false);
									safeSet("app_install_dismissed", "1");
								}
								deferredInstallPrompt = null;
							} else if (isIos) {
								notify.success("Tap the Share button ↑ then 'Add to Home Screen'");
							}
						}}
						className="flex-1 text-left text-xs font-medium text-info hover:underline"
					>
						{isIos ? "Add to Home Screen" : "Install app"}
					</button>
					<button
						type="button"
						onClick={handleDismissInstall}
						className="ml-1 p-0.5 text-foreground-muted hover:text-info"
						aria-label="Dismiss"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</div>
			)}
		</div>
	);
}
